import {Button, Col, Popover, Row, Table} from "antd";
import React, {useRef} from "react";
import sorters from "../../util/sorters";
import moment from "moment/moment";
import {CONVERT_DATE_FORMAT, PREFERRED_DATE_FORMAT} from "../../util/time";
import {useDynamicTableHeight} from "../../util/useDynamicTableHeight";
import {getPaginationConfig} from "../../util/showAllPagination";

const HourLogTable = ({
                        hourLog,
                        loading,
                        projectSearch,
                        isModal
                      }) => {
  const tableContainerRef = useRef(null);
  const tableHeight = useDynamicTableHeight(tableContainerRef);

  const columns = [
    {
      title: 'Worker',
      width: "270px",
      sorter: (a, b) => a.worker && b.worker ? a.worker.email.localeCompare(b.worker.email) : 0,
      render: r => r.worker ? r.worker.email : '-'
    },
    {
      title: 'Project Code',
      width: "160px",
      sorter: (a, b) => sorters.string(a.projectCode, b.projectCode),
      render: r => (
        <Popover
          placement="topLeft"
          overlayStyle={{
            width: "45vw"
          }}
          trigger="hover,focus,click"
          content={(
            <>
              <Row>
                <Col span={6}><b>Project Name</b></Col>
                <Col span={18}>{r.projectName}</Col>
              </Row>
              <Row>
                <Col span={6}><b>Billable</b></Col>
                <Col span={18}>{r.engagementBillable}</Col>
              </Row>
              <Row>
                <Col span={6}><b>Time Contract Terms</b></Col>
                <Col span={18}>{r.timeContractTerms}</Col>
              </Row>
              {projectSearch && (<>
                <Row>
                  <Col span={6}><b>Export ID</b></Col>
                  <Col span={18}>{r.export.ID}</Col>
                </Row>
                <Row>
                  <Col span={6}><b>Export Filename</b></Col>
                  <Col span={18}>{r.export.filename}</Col>
                </Row>
              </>)}
            </>
          )}
        >
          <Button>{r.projectCode}</Button>
        </Popover>
      ),
    },
    {
      title: 'Chargeable',
      width: "100px",
      sorter: (a, b) => sorters.string(a.timeCardChargeable, b.timeCardChargeable),
      dataIndex: 'timeCardChargeable'
    },
    {
      title: 'Time Off Reason',
      ellipsis: true,
      sorter: (a, b) => sorters.string(a.timeOffReason, b.timeOffReason),
      render: r => r.timeOffReason ? r.timeOffReason : '-'
    },
    {
      title: 'Work Date',
      width: "100px",
      sorter: (a, b) => sorters.date(a.workDate, b.workDate),
      render: r => moment.utc(r.workDate).format(PREFERRED_DATE_FORMAT)
    },
    {
      title: 'Approved By',
      sorter: (a, b) => sorters.string(a.approvedBy, b.approvedBy),
      render: r => r.approvedBy ? r.approvedBy : '-'
    },
    {
      title: 'Approved On',
      width: "120px",
      // stored in DB as VARCHAR
      sorter: (a, b) => sorters.date(moment(a.approvedOn, CONVERT_DATE_FORMAT), moment(b.approvedOn, CONVERT_DATE_FORMAT)),
      render: r => r.approvedOn ? moment(r.approvedOn, CONVERT_DATE_FORMAT).format(PREFERRED_DATE_FORMAT) : '-'
    },
    {
      title: 'Status',
      width: "100px",
      dataIndex: 'status',
      sorter: (a, b) => sorters.string(a.status, b.status)
    },
    {
      title: 'Error Type',
      width: "100px",
      dataIndex: 'errorType',
      sorter: (a, b) => sorters.string(a.errorType, b.errorType)
    },
    {
      title: 'Message',
      ellipsis: true,
      sorter: (a, b) => sorters.string(a.message, b.message),
      render: r => r.message ? r.message : '-'
    },
    {
      title: 'Hours',
      width: "60px",
      render: r => r.personHours.toFixed(2),
      align: 'right',
      sorter: (a, b) => a.personHours - b.personHours
    },
    {
      title: 'Total Hours',
      width: "110px",
      render: r => r.totalPersonHours.toFixed(2),
      align: 'right',
      sorter: (a, b) => a.totalPersonHours - b.totalPersonHours
    },
    {
      title: 'Category',
      width: "85px",
      sorter: (a, b) => sorters.string(a.hoursCategoryCode, b.hoursCategoryCode),
      dataIndex: 'hoursCategoryCode'
    },
    {
      title: 'Previous',
      width: "100px",
      render: r => r.previous ? (
        <Popover
          overlayStyle={{
            width: "35vw"
          }}
          placement="topLeft"
          trigger="hover,focus,click"
          content={(
            <>
              <Row>
                <Col span={6}><b>Billable</b></Col>
                <Col span={18}>{r.previous.engagementBillable}</Col>
              </Row>
              <Row>
                <Col span={6}><b>Time Contract Terms</b></Col>
                <Col span={18}>{r.previous.timeContractTerms}</Col>
              </Row>
              <Row>
                <Col span={6}><b>Approved By</b></Col>
                <Col span={18}>{r.previous.approvedBy ? r.previous.approvedBy : '-'}</Col>
              </Row>
              <Row>
                <Col span={6}><b>Approved On</b></Col>
                <Col span={18}>{r.previous.approvedOn ?
                  moment.utc(r.previous.approvedOn).format(PREFERRED_DATE_FORMAT) : '-'}</Col>
              </Row>
              <Row>
                <Col span={6}><b>Status</b></Col>
                <Col span={18}>{r.previous.status}</Col>
              </Row>
              <Row>
                <Col span={6}><b>Error Type</b></Col>
                <Col span={18}>{r.previous.errorType}</Col>
              </Row>
              <Row>
                <Col span={6}><b>Message</b></Col>
                <Col span={18}>{r.previous.message ? r.previous.message : '-'}</Col>
              </Row>
              <Row>
                <Col span={6}><b>Hours</b></Col>
                <Col span={18}>{r.previous.personHours}</Col>
              </Row>
              <Row>
                <Col span={6}><b>Total Hours</b></Col>
                <Col span={18}>{r.previous.totalPersonHours}</Col>
              </Row>
              <Row>
                <Col span={6}><b>Category Code</b></Col>
                <Col span={18}>{r.previous.hoursCategoryCode}</Col>
              </Row>
            </>
          )}
        >
          <Button>Previous</Button>
        </Popover>) : '-'
    },
  ];

  console.log(isModal);

  return (
    <>
      {isModal ? (
        <Table
          rowKey="ID"
          dataSource={hourLog}
          columns={columns}
          pagination={getPaginationConfig(hourLog.length)}
          size="small"
          loading={loading}
          scroll={{y: 'calc(100vh - 210px)'}}
        />
      ) : (
        <Table
          ref={tableContainerRef}
          rowKey="ID"
          dataSource={hourLog}
          columns={columns}
          pagination={getPaginationConfig(hourLog.length)}
          size="small"
          loading={loading}
          scroll={{y: tableHeight}}
        />
      )
      }
    </>
  );
}

export {HourLogTable};